/**
 * PartBundlePicker — "Add bundle" for the parts of a work-order / quote service (#3350 phase 1, #3034).
 *
 * One selection reads the bundle's expansion (parts, quantities, categories), prices every part with the
 * server's one resolver for THIS work order (customer price, campaign, category margin, catalog; see
 * `useWorkOrderPartPricing`) and hands the lines plus the resolved NET prices to the caller, which appends
 * them to its own draft as ordinary part rows. The expansion's own catalog prices are not used. This
 * component saves nothing and touches no stock: the caller's normal autosave (with its version token and
 * conflict handling) is the only write path.
 *
 * All or nothing: if any part of the bundle is no longer available, or any price cannot be resolved, the
 * whole selection is refused with a message and nothing is inserted. One selection inserts at most once (a
 * second click while one is running is ignored).
 *
 * One selection belongs to the owner (tenant, user, session family, generation, rights) and the work order it was
 * started under. The WHOLE async chain is fenced against that: the expansion read, its success AND its failure
 * (so a refusal of the previous owner never shows its part names or reaches a notification), each pricing call, and the
 * final insert. The picker is also keyed by owner and work order, so another owner or work order gets a fresh dialog
 * in the same render: no stale list, refusal, busy state or source detail, and an old operation can never be revived
 * by returning to the previous owner or work order (A -> B -> A).
 *
 * Bundle strings live in the `inventory` namespace (`bundles.picker.*`); the pricing messages in `workorders`.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory2';
import SearchIcon from '@mui/icons-material/Search';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useDebounce } from '@/hooks/useDebounce';
import { useNotify } from '@/hooks/useNotify';
import {
  fetchPartBundleExpansion,
  usePartBundleOptions,
  type PartBundleExpansion,
  type PartBundleListItem,
} from '@/hooks/useInventory';
import { describeApiError } from '@/lib/describeApiError';
import { useAuthStore } from '@/stores/authStore';
import {
  accessKey,
  OwnerChangedError,
  fenceHolds,
  isSessionCancel,
  takeFence,
  usePricingAccess,
  type PricingFence,
} from '@/features/inventory/pricing/pricingSession';
import {
  PART_RESOLVE_PERMISSIONS,
  PartPriceResolutionFailure,
  WORK_ORDER_READ_PERMISSION,
  asPartPriceFailure,
  partPriceFailureLabelKey,
  useWorkOrderPartPricing,
  type ResolvedPartLinePrice,
} from '@/hooks/useWorkOrderPartPricing';
import { unavailablePartNames } from './unavailableParts';

export interface PartBundlePickerProps {
  /** The work order the lines are priced for (the booking context of the resolution). */
  workOrderId: string;
  /**
   * Receives every line of the chosen bundle at once with the resolved NET price of each part (by part id); it must
   * append them as ordinary part rows.
   */
  onInsert: (expansion: PartBundleExpansion, prices: ReadonlyMap<string, ResolvedPartLinePrice>) => void;
  disabled?: boolean;
}

/**
 * The public picker: the owner, the rights pricing needs and the work order are its identity. Any change of them remounts the
 * inner picker in the render that sees it.
 */
export function PartBundlePicker(props: PartBundlePickerProps) {
  const access = usePricingAccess();
  const mayReadWorkOrders = useAuthStore((state) => state.hasPermission(WORK_ORDER_READ_PERMISSION));
  const identity = `${accessKey(access)}|${mayReadWorkOrders ? 'wo-read' : 'no-wo-read'}|${props.workOrderId}`;
  return <PartBundlePickerInner key={identity} access={access} {...props} />;
}

function PartBundlePickerInner({
  access,
  workOrderId,
  onInsert,
  disabled = false,
}: PartBundlePickerProps & { access: ReturnType<typeof usePricingAccess> }) {
  const { t } = useTranslation('inventory');
  const { t: tw } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const canReadInventory = useAuthStore((state) => state.hasPermission('inventory.read'));
  const pricing = useWorkOrderPartPricing(workOrderId);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [insertingId, setInsertingId] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const mountedRef = useRef(true);
  // One selection at a time, decided synchronously: `insertingId` is state and lags a second click of the same tick.
  const busyRef = useRef(false);
  // Identity of the running selection: only the selection that owns it may report or clear the busy state.
  const operationRef = useRef(0);
  const workOrderIdRef = useRef(workOrderId);
  workOrderIdRef.current = workOrderId;
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // A selection still in flight belongs to a picker that is gone (other owner, work order or unmount).
      operationRef.current += 1;
    };
  }, []);

  const query = useDebounce(search.trim(), 250);
  const bundles = usePartBundleOptions(query ? { q: query } : {}, { enabled: open && canReadInventory });

  if (!canReadInventory) return null;

  const close = () => {
    if (insertingId) return;
    setOpen(false);
    setSearch('');
    setRefusal(null);
  };

  const choose = async (bundle: PartBundleListItem) => {
    if (busyRef.current || insertingId) return;
    busyRef.current = true;
    const operation = ++operationRef.current;
    const startedFor = workOrderIdRef.current;
    // The selection's owner: the live session of the render that offered it, taken before anything is read or sent.
    let fence: PricingFence | null = null;
    // Still the selection that asked: mounted, the same work order, not superseded, and the session, user, tenant, generation,
    // lock and rights it began under are still the live ones. Checked before every use of anything the chain produced.
    const current = () =>
      mountedRef.current &&
      workOrderIdRef.current === startedFor &&
      operationRef.current === operation &&
      fence !== null &&
      fenceHolds(fence);
    setInsertingId(bundle.id);
    setRefusal(null);
    try {
      // Refused before anything is read or sent: this caller may not price a work order's parts, or the session moved on.
      if (!pricing.canResolve) throw new PartPriceResolutionFailure('forbidden');
      try {
        fence = takeFence(access, PART_RESOLVE_PERMISSIONS);
      } catch (error) {
        // Nothing was sent. A session that moved on since this render shows nothing; a right lost meanwhile is a refusal.
        if (error instanceof OwnerChangedError) return;
        throw new PartPriceResolutionFailure('forbidden');
      }
      const expansion = await fetchPartBundleExpansion(bundle.id);
      if (!current()) return;
      const prices = await pricing.resolve(expansion.lines.map((line) => line.partId));
      if (!current()) return;
      onInsert(expansion, prices);
      notify.success(
        t('bundles.picker.inserted'),
        t('bundles.picker.insertedDesc', { count: expansion.lines.length, name: expansion.bundleName }),
      );
      setOpen(false);
      setSearch('');
    } catch (error) {
      // The owner, a right, the work order or the picker changed under the chain: the error concerns a screen that is gone, and
      // its content (part names, reasons) is not read, shown or notified.
      if (isSessionCancel(error)) return;
      if (fence !== null ? !current() : !mountedRef.current || operationRef.current !== operation) return;
      const names = unavailablePartNames(error);
      const failure = asPartPriceFailure(error);
      const message =
        names !== null
          ? t('bundles.picker.unavailable', { name: bundle.name, parts: names.join(', ') })
          : failure !== null
            ? tw('detail.partPricing.bundleNotInserted', { reason: tw(partPriceFailureLabelKey(failure.reason)) })
            : describeApiError(error, tc, { fallback: t('bundles.picker.failed') });
      setRefusal(message);
      notify.error(tc('error'), message);
    } finally {
      // Only the selection that owns the busy state clears it, so a stale completion can never release a newer action.
      if (operationRef.current === operation) {
        busyRef.current = false;
        if (mountedRef.current) setInsertingId(null);
      }
    }
  };

  const rows = bundles.data?.data ?? [];

  return (
    <>
      <Tooltip title={t('bundles.picker.button')}>
        <span>
          <Button
            size="small"
            startIcon={<InventoryIcon fontSize="small" />}
            endIcon={<AddIcon fontSize="small" />}
            onClick={() => setOpen(true)}
            disabled={disabled}
            aria-label={t('bundles.picker.button')}
          >
            {t('bundles.picker.button')}
          </Button>
        </span>
      </Tooltip>

      <ResponsiveDialog
        open={open}
        onClose={close}
        maxWidth="xs"
        fullWidth
        mobileTitle={t('bundles.picker.title')}
        aria-labelledby="part-bundle-picker-title"
      >
        <DialogTitle id="part-bundle-picker-title">{t('bundles.picker.title')}</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={t('bundles.picker.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ mb: 1, mt: 0.5 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              },
              htmlInput: { 'aria-label': t('bundles.picker.searchPlaceholder') },
            }}
          />
          {refusal && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {refusal}
            </Alert>
          )}
          {bundles.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} aria-label={tc('loading')} />
            </Box>
          ) : bundles.isError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => void bundles.refetch()}>
                  {t('bundles.picker.retry')}
                </Button>
              }
            >
              {t('bundles.picker.loadFailed')}
            </Alert>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontStyle: 'italic' }}>
              {t('bundles.picker.empty')}
            </Typography>
          ) : (
            <List dense disablePadding aria-label={t('bundles.picker.title')}>
              {rows.map((bundle) => (
                <ListItemButton
                  key={bundle.id}
                  onClick={() => void choose(bundle)}
                  disabled={insertingId !== null}
                  aria-label={bundle.name}
                >
                  <ListItemText
                    primary={bundle.name}
                    secondary={
                      [
                        bundle.description,
                        t('bundles.picker.partCount', { count: bundle.itemCount }),
                        bundle.unavailableCount > 0 ? t('bundles.picker.unavailableBadge') : null,
                      ]
                        .filter((part): part is string => typeof part === 'string' && part !== '')
                        .join(' · ')
                    }
                  />
                  {insertingId === bundle.id && <CircularProgress size={18} aria-label={t('bundles.picker.inserting')} />}
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={insertingId !== null}>
            {tc('close')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  );
}
